/**
 * dir-w | backOffice
 * Menu navigations service
 * - collections
 * - interaction (read|delete|restore)
 * - create
 * - update
 * - datalist
 *
 * Last update : January 22, 2024 | @rizkiprasetya
 */

import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { Configuration } from '../config/configuration';
import { BaseCrudRepository } from '../repositories/base-crud.repository';
import { IHttpCollections, IHttpResponse } from '../interfaces/http.interface';
import { enumInteractionMethod } from '../app.enum';

@Injectable({
  providedIn: 'root',
})
export class MenuService extends BaseCrudRepository implements OnDestroy {
  // class variables
  private subject = new Subject<any>();
  private moduleEndPoint: string = `${Configuration.apiURL}/backOffice/developer/menus`;

  constructor(_http: HttpClient) {
    super(_http);
  }

  ngOnDestroy(): void {
    this.subject.unsubscribe();
  }

  /**
   * Sent an update messaget to subject
   * @param message string message
   */
  sendUpdate(message: string): void {
    //the component that wants to update something, calls this fn
    //next() will feed the value in Subject
    this.subject.next({ text: message });
  }

  /**
   * Get updates from subject via observable
   * @returns Observable<any>
   */
  getUpdate(): Observable<any> {
    // the receiver component calls this function
    // it returns as an observable to which the receiver funtion will subscribe
    return this.subject.asObservable();
  }

  /**
   * Get list of menu navigation data collections
   * @param _listParams Array<{ key: string; value: string }>
   * @returns `Observable<IHttpCollections>`
   */
  public collections(
    __listParams: Array<{ key: string; value: string }>
  ): Observable<IHttpCollections> {
    return this.get(this.moduleEndPoint, __listParams);
  }

  /**
   * Http RESTful API for method `patch`(restore), `delete` and `get`\
   * No `HttpParams` or `Body`, just pass the `ID` on the URL endPoint
   * @param __interactionMethod enumInteractionMethod(`READ`|`DELETE`|`RESTORE`)
   * @param __id `UUID` format
   * @returns `Observable<IHttpResponse>`
   */
  public interaction(
    __interactionMethod: enumInteractionMethod,
    __id: string
  ): Observable<IHttpResponse> {
    const _endPoint: string = `${this.moduleEndPoint}/${__interactionMethod}/${__id}`;
    if (__interactionMethod === enumInteractionMethod.RESTORE) {
      return this.patch(_endPoint, null);
    } else if (__interactionMethod === enumInteractionMethod.DELETE) {
      return this.delete(_endPoint);
    } else {
      return this.get(_endPoint, []);
    }
  }

  /**
   * Create new navigation record
   * @param __parent_id string
   * @param __title string
   * @param __path_url string
   * @param __icon_class string
   * @param __order number
   * @returns `Observable<IHttpResponse>`
   */
  public create(
    __parent_id: string,
    __title: string,
    __path_url: string,
    __icon_class: string,
    __order: number
  ): Observable<IHttpResponse> {
    return this.post(`${this.moduleEndPoint}/create`, {
      parent_id: __parent_id,
      title: __title,
      path_url: __path_url,
      icon_class: __icon_class,
      order: __order,
    });
  }

  /**
   * Update navigation record
   * @param __id UUID format
   * @param __parent_id string
   * @param __title string
   * @param __path_url string
   * @param __icon_class string
   * @param __order number
   * @returns `Observable<IHttpResponse>`
   */
  public update(
    __id: string,
    __parent_id: string,
    __title: string,
    __path_url: string,
    __icon_class: string,
    __order: number
  ): Observable<IHttpResponse> {
    return this.put(`${this.moduleEndPoint}/update/${__id}`, {
      parent_id: __parent_id,
      title: __title,
      path_url: __path_url,
      icon_class: __icon_class,
      order: __order,
    });
  }

  /**
   * Get list of menu navigation by parent
   * @param __parent_id string
   * @returns `Observable<IHttpResponse>`
   */
  public datalist(__parent_id: string): Observable<IHttpResponse> {
    return this.get(`${this.moduleEndPoint}/datalist/${__parent_id}`, []);
  }
}
